import { useState } from 'react';
import { Link } from 'react-router-dom';
import { mockPartners } from '../../../mocks/partners';

const roleFilters = [
  { id: 'all', label: 'すべて', icon: 'ri-team-line' },
  { id: '看護師', label: '看護師', icon: 'ri-nurse-line' },
  { id: '先輩ママ', label: '先輩ママ', icon: 'ri-parent-line' },
  { id: '保育士', label: '保育士', icon: 'ri-user-heart-line' },
  { id: '理学療法士', label: '理学療法士', icon: 'ri-walk-line' },
  { id: '社会福祉士', label: '社会福祉士', icon: 'ri-hand-heart-line' },
];

const getRoleColor = (role: string) => {
  switch (role) {
    case '看護師': return { bg: 'bg-teal-100', text: 'text-teal-700', accent: 'from-teal-400 to-teal-600' };
    case '先輩ママ': return { bg: 'bg-rose-100', text: 'text-rose-700', accent: 'from-rose-400 to-rose-600' };
    case '保育士': return { bg: 'bg-amber-100', text: 'text-amber-700', accent: 'from-amber-400 to-amber-600' };
    case '理学療法士': return { bg: 'bg-emerald-100', text: 'text-emerald-700', accent: 'from-emerald-400 to-emerald-600' };
    case '社会福祉士': return { bg: 'bg-indigo-100', text: 'text-indigo-700', accent: 'from-indigo-400 to-indigo-600' };
    default: return { bg: 'bg-gray-100', text: 'text-gray-700', accent: 'from-gray-400 to-gray-600' };
  }
};

const miyagiSupportOrganizations = [
  {
    name: '宮城県医療的ケア児等支援センター',
    description: '医療的ケア児とその家族への総合的な相談支援',
    url: 'https://www.pref.miyagi.jp/soshiki/shougai/iryoutekikeajishien.html',
    icon: 'ri-hospital-line',
    color: 'teal'
  },
  {
    name: '宮城県発達障害者支援センター えくぼ',
    description: '発達障害に関する相談・支援',
    url: 'https://www.pref.miyagi.jp/soshiki/shougai/ekubo.html',
    icon: 'ri-heart-pulse-line',
    color: 'rose'
  },
  {
    name: '宮城県障害者総合相談センター',
    description: '障害のある方とその家族への総合相談窓口',
    url: 'https://www.pref.miyagi.jp/soshiki/shougai/soudan-center.html',
    icon: 'ri-customer-service-2-line',
    color: 'purple'
  },
  {
    name: '仙台市障害者総合支援センター',
    description: '仙台市の障害児・者への相談支援',
    url: 'https://www.city.sendai.jp/shogaishien/kurashi/kenkotofukushi/shogaisha/shien/center.html',
    icon: 'ri-building-line',
    color: 'indigo'
  },
  {
    name: '宮城県小児在宅医療推進事業',
    description: '小児在宅医療に関する情報提供と支援',
    url: 'https://www.pref.miyagi.jp/soshiki/iryou/syounizaitaku.html',
    icon: 'ri-home-heart-line',
    color: 'emerald'
  },
  {
    name: '宮城県保健福祉部 障害福祉課',
    description: '障害福祉サービスに関する情報',
    url: 'https://www.pref.miyagi.jp/soshiki/shougai/',
    icon: 'ri-government-line',
    color: 'amber'
  }
];

export default function PartnerListPage() {
  const [selectedRole, setSelectedRole] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const filteredPartners = selectedRole === 'all'
    ? mockPartners
    : mockPartners.filter(p => p.partner_role === selectedRole);

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-purple-50 to-teal-50">
      {/* ナビゲーション */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-rose-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center">
                <i className="ri-ship-line text-white text-lg sm:text-xl"></i>
              </div>
              <span className="text-base sm:text-xl font-bold bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent">
                みやぎスマイル・ポート
              </span>
            </Link>
            {/* デスクトップメニュー */}
            <div className="hidden md:flex items-center space-x-4">
              <Link to="/" className="text-gray-600 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">ホーム</Link>
              <Link to="/board" className="text-gray-600 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">みんなの休憩室</Link>
              <Link to="/manual" className="text-gray-600 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">お守りマニュアル</Link>
              <Link to="/partners" className="text-teal-600 font-medium whitespace-nowrap cursor-pointer">スマイル・パートナー</Link>
            </div>
            {/* モバイルメニューボタン */}
            <button
              className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>
        {/* モバイルメニュー */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-3 space-y-1">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors cursor-pointer">ホーム</Link>
              <Link to="/board" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors cursor-pointer">みんなの休憩室</Link>
              <Link to="/manual" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors cursor-pointer">お守りマニュアル</Link>
              <Link to="/partners" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-medium text-teal-600 bg-teal-50 rounded-lg cursor-pointer">スマイル・パートナー</Link>
            </div>
          </div>
        )}
      </nav>

      {/* ヒーローセクション */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/0d72d3f442f1838be727741069b4fd4e.jpg"
            alt="スマイル・パートナー"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-teal-900/60 via-teal-800/50 to-rose-900/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 lg:py-20 text-center w-full">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-4 sm:mb-6 whitespace-nowrap text-xs sm:text-sm">
            <i className="ri-user-star-line"></i>
            <span className="font-semibold">Smile Partner</span>
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white mb-3 sm:mb-4">
            スマイル・パートナー
          </h1>
          <p className="text-base sm:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            看護師、保育士、先輩ママなど、あなたに寄り添う「伴走者」たち。<br className="hidden sm:block" />
            共感とアドバイスで、あなたの子育てをサポートします。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mt-6 sm:mt-8">
            <div className="flex items-center gap-2 text-white/80">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-team-line text-xs sm:text-sm"></i>
              </div>
              <span className="text-xs sm:text-sm font-medium">{mockPartners.length}名のパートナー</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-time-line text-xs sm:text-sm"></i>
              </div>
              <span className="text-xs sm:text-sm font-medium">平日 10:00〜17:00 見守り</span>
            </div>
          </div>
          <div className="mt-6 sm:mt-8">
            <Link
              to="/partner/recruit"
              className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-white text-teal-700 rounded-full hover:shadow-lg transition-all cursor-pointer whitespace-nowrap font-semibold text-sm sm:text-base"
            >
              <i className="ri-user-add-line"></i>
              パートナーになる
            </Link>
          </div>
        </div>
      </section>

      {/* メインコンテンツ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* 役職フィルター */}
        <div className="bg-white rounded-2xl shadow-sm p-2 mb-8 sm:mb-10">
          <div className="flex flex-wrap gap-2">
            {roleFilters.map(filter => (
              <button
                key={filter.id}
                onClick={() => setSelectedRole(filter.id)}
                className={`flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap text-xs sm:text-sm ${
                  selectedRole === filter.id
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <i className={filter.icon}></i>
                <span>{filter.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* パートナーカード一覧 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {filteredPartners.map((partner) => {
            const roleColor = getRoleColor(partner.partner_role);
            const isExpanded = expandedId === partner.id;

            return (
              <div
                key={partner.id}
                className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group"
              >
                {/* カード上部：グラデーションバー */}
                <div className={`h-2 bg-gradient-to-r ${roleColor.accent}`}></div>

                <div className="p-4 sm:p-6">
                  {/* プロフィール情報 */}
                  <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-5">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden flex-shrink-0 shadow-md group-hover:shadow-lg transition-shadow">
                      <img
                        src={partner.avatar_url}
                        alt={partner.nickname}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">{partner.nickname}</h3>
                      <span className={`inline-flex items-center gap-1 px-2.5 sm:px-3 py-1 rounded-full text-xs font-semibold ${roleColor.bg} ${roleColor.text}`}>
                        <i className={roleFilters.find(r => r.id === partner.partner_role)?.icon || 'ri-user-star-line'}></i>
                        {partner.partner_role}
                      </span>
                      <div className="flex items-center gap-1 mt-2 text-gray-500 text-xs">
                        <i className="ri-map-pin-2-line"></i>
                        <span>{partner.area}</span>
                      </div>
                    </div>
                  </div>

                  {/* 自己紹介 */}
                  <p className={`text-gray-600 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4 ${isExpanded ? '' : 'line-clamp-3'}`}>
                    {partner.bio}
                  </p>
                  {partner.bio.length > 80 && (
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : partner.id)}
                      className="text-teal-600 text-xs font-medium hover:text-teal-700 cursor-pointer mb-3 sm:mb-4 flex items-center gap-1"
                    >
                      {isExpanded ? (
                        <>閉じる <i className="ri-arrow-up-s-line"></i></>
                      ) : (
                        <>もっと読む <i className="ri-arrow-down-s-line"></i></>
                      )}
                    </button>
                  )}

                  {/* 専門分野タグ */}
                  <div className="flex flex-wrap gap-1.5 mb-4 sm:mb-5">
                    {partner.expertise.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 sm:px-2.5 py-1 bg-gray-100 text-gray-600 rounded-md text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* 統計情報 */}
                  <div className="flex items-center justify-between pt-3 sm:pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-chat-3-line text-xs sm:text-sm"></i>
                        </div>
                        <span className="text-xs font-medium">{partner.reply_count}件</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-rose-400">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-heart-3-fill text-xs sm:text-sm"></i>
                        </div>
                        <span className="text-xs font-medium">{partner.thanks_count}</span>
                      </div>
                    </div>
                    <Link
                      to={`/partners/${partner.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                      className="flex items-center gap-1 text-teal-600 text-xs font-semibold hover:text-teal-700 cursor-pointer whitespace-nowrap"
                    >
                      プロフィール
                      <i className="ri-arrow-right-s-line"></i>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredPartners.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-8 sm:p-12 text-center">
            <i className="ri-user-search-line text-4xl sm:text-5xl text-gray-300 mb-4"></i>
            <p className="text-sm sm:text-base text-gray-600">該当するパートナーが見つかりませんでした</p>
          </div>
        )}

        {/* CTA セクション */}
        <section className="py-12 sm:py-20 bg-gradient-to-br from-teal-500 to-teal-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 sm:mb-8">
              <i className="ri-user-star-line text-white text-3xl sm:text-4xl"></i>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6">
              あなたも
              <br />
              スマイル・パートナーに
            </h2>
            <p className="text-base sm:text-lg text-white/90 mb-8 sm:mb-12 leading-relaxed">
              看護師、保育士、先輩ママなど、医療的ケア児の家族を支える活動をし
              <br className="hidden sm:block" />
              ています。あなたの経験と知識が、誰かの力になります。
            </p>

            <div className="space-y-4 sm:space-y-6 mb-8 sm:mb-12">
              <div className="flex items-center justify-center gap-3 text-white">
                <i className="ri-check-line text-2xl"></i>
                <span className="text-sm sm:text-base">平日10:00〜17:00の見守り時間内で活動</span>
              </div>
              <div className="flex items-center justify-center gap-3 text-white">
                <i className="ri-check-line text-2xl"></i>
                <span className="text-sm sm:text-base">研修プログラムで安心してスタート</span>
              </div>
              <div className="flex items-center justify-center gap-3 text-white">
                <i className="ri-check-line text-2xl"></i>
                <span className="text-sm sm:text-base">共感を大切にした対応ガイドライン完備</span>
              </div>
            </div>

            <Link
              to="/partner/recruit"
              className="inline-flex items-center gap-2 bg-white text-teal-700 px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap shadow-xl"
            >
              詳しく見る
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        {/* 方針セクション */}
        <div className="mt-8 sm:mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
          <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 text-center">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <i className="ri-heart-3-fill text-rose-500 text-xl sm:text-2xl"></i>
            </div>
            <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">共感を最優先</h3>
            <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
              「正論」ではなく「その気持ち、わかるよ」を大切にします。まずは気持ちを受け止めます。
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 text-center">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <i className="ri-hand-heart-line text-teal-500 text-xl sm:text-2xl"></i>
            </div>
            <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">心の荷降ろし</h3>
            <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
              話を聞き、気持ちを受け止めることから始めます。安心できる場所を一緒に作ります。
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 text-center">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <i className="ri-shield-check-line text-amber-500 text-xl sm:text-2xl"></i>
            </div>
            <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">安全な環境</h3>
            <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
              否定されることはありません。ここはあなたの避難所です。安心してお話しください。
            </p>
          </div>
        </div>

        {/* 宮城県の支援機関リンク */}
        <div className="mt-12 sm:mt-16 bg-white rounded-2xl sm:rounded-3xl shadow-sm p-6 sm:p-10">
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-3 sm:mb-4 whitespace-nowrap text-xs sm:text-sm">
              <i className="ri-links-line"></i>
              <span className="font-semibold">Support Links</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">
              宮城県の支援機関
            </h2>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
              医療的ケア児とご家族を支援する宮城県内の公的機関をご紹介します
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {miyagiSupportOrganizations.map((org, index) => (
              <a
                key={index}
                href={org.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-5 sm:p-6 border border-gray-100 hover:shadow-lg transition-all group cursor-pointer"
              >
                <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-${org.color}-100 rounded-xl flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform`}>
                  <i className={`${org.icon} text-${org.color}-600 text-xl sm:text-2xl`}></i>
                </div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                  {org.name}
                </h3>
                <p className="text-gray-600 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">
                  {org.description}
                </p>
                <div className="flex items-center gap-2 text-teal-600 text-xs sm:text-sm font-medium">
                  <span>詳しく見る</span>
                  <i className="ri-external-link-line group-hover:translate-x-1 transition-transform"></i>
                </div>
              </a>
            ))}
          </div>

          <div className="mt-6 sm:mt-8 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-5 sm:p-6 text-center">
            <i className="ri-information-line text-teal-600 text-2xl sm:text-3xl mb-2 sm:mb-3"></i>
            <p className="text-gray-700 text-xs sm:text-sm leading-relaxed">
              各機関の詳細な情報や最新の支援内容については、リンク先の公式サイトをご確認ください。<br className="hidden sm:block" />
              ご不明な点がございましたら、スマイル・パートナーにもお気軽にご相談ください。
            </p>
          </div>
        </div>
      </div>

      {/* フッター */}
      <footer className="bg-gradient-to-r from-teal-700 to-teal-900 text-white mt-12 sm:mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-3 sm:mb-4">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-ship-line text-white text-lg sm:text-xl"></i>
                </div>
                <span className="text-lg sm:text-xl font-bold">みやぎスマイル・ポート</span>
              </div>
              <p className="text-teal-100 text-xs sm:text-sm">医療的ケア児と家族の港</p>
            </div>
            <div>
              <h4 className="font-bold mb-2 sm:mb-3 text-sm sm:text-base">リンク</h4>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-teal-100">
                <li><Link to="/" className="hover:text-white transition-colors cursor-pointer">ホーム</Link></li>
                <li><Link to="/board" className="hover:text-white transition-colors cursor-pointer">みんなの休憩室</Link></li>
                <li><Link to="/manual" className="hover:text-white transition-colors cursor-pointer">お守りマニュアル</Link></li>
                <li><Link to="/partners" className="hover:text-white transition-colors cursor-pointer">スマイル・パートナー</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-2 sm:mb-3 text-sm sm:text-base">お問い合わせ</h4>
              <p className="text-xs sm:text-sm text-teal-100">support@smile-port.jp</p>
              <p className="text-xs sm:text-sm text-teal-100 mt-2">平日 10:00〜17:00</p>
            </div>
          </div>
          <div className="border-t border-teal-600 pt-6 sm:pt-8 text-center text-xs sm:text-sm text-teal-100">
            <p>
              &copy; 2025 みやぎスマイル・ポート. All rights reserved. |{' '}
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
